import { useCallback, useEffect, useReducer, useRef } from 'react'
import { useSettingsVersionQuery } from '@paintscout/api'
import { useClientOptions } from '@paintscout/ui-business'
import { useAppDocsFresh } from 'src/components/App/AuthorizeUser/AppDocsFreshContext'
import type { CurrentSettingsVersion } from 'src/util/isSettingsOutdated'
import { isSettingsOutdated } from 'src/util/isSettingsOutdated'
import { usePageVisibility } from './usePageVisibility'
import { usePollingInterval } from './usePollingInterval'

const POLL_INTERVAL = 5 * 60_000
/** A tab that comes back to the front re-checks at most this often. */
const VISIBLE_REFETCH_THROTTLE = 60_000

/**
 * Whether another session (another user, tab, admin or masquerade edit, or the OpenAPI) changed
 * the company settings this session loaded (#25436).
 *
 * The loaded values come from `useClientOptions()`, which this tab's own saves update. Those saves
 * also write the new values into this query's cached result (`syncSettingsVersionCache`), so a save
 * here never flags itself (ENG-2701). The result is worked out on every render, not latched: when a
 * later cache write brings this tab up to date, it goes back to false.
 *
 * Other code can also load newer settings into this tab without going through a save or sync:
 * opening the resource center, a billing checkout, or a user edit refetches `appDocs`. The last
 * check is then older than the copy on screen, so comparing them would show the notice although the
 * tab already has the latest settings. When the loaded copy changes after the last check, this
 * reports not outdated and checks again; the answer to that check decides.
 *
 * Returns the server values as `versionKey` so a caller can tell one change from the next (to
 * dismiss one change and still show a newer one).
 */
export function useSettingsOutdated(): { outdated: boolean; versionKey: string | null } {
  const { options, rates } = useClientOptions()
  // Skip until appDocs has come back from the network. Before that, `useClientOptions()` can hold
  // the IndexedDB copy from an earlier visit, which would look outdated against the server.
  const appDocsFresh = useAppDocsFresh()
  const pollInterval = usePollingInterval(POLL_INTERVAL)
  const isVisible = usePageVisibility()

  const { data, refetch } = useSettingsVersionQuery({
    fetchPolicy: 'network-only',
    skip: !appDocsFresh,
    pollInterval
  })

  // The query fetches as soon as it is no longer skipped, so that counts as the first check.
  const lastCheckRef = useRef(0)
  useEffect(() => {
    if (appDocsFresh) {
      lastCheckRef.current = Date.now()
    }
  }, [appDocsFresh])

  // The loaded copy the last check is known to be at least as new as. A new query result counts as
  // a check of the copy loaded when it arrived; a re-check counts for the copy it was started for.
  const loadedKey = JSON.stringify([options?.settingsUpdated ?? null, rates?._rev ?? null])
  const lastDataRef = useRef(data)
  const checkedForRef = useRef(loadedKey)
  if (data !== lastDataRef.current) {
    lastDataRef.current = data
    checkedForRef.current = loadedKey
  }
  const loadedSinceCheck = loadedKey !== checkedForRef.current
  const loadedKeyRef = useRef(loadedKey)
  loadedKeyRef.current = loadedKey
  const [, rerender] = useReducer((n: number) => n + 1, 0)

  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /**
   * Asks the server again. The answer counts as a check of the copy loaded when it was asked.
   * A failed request counts too, so the hook falls back to the last answer it has instead of hiding
   * the notice until the next change.
   */
  const check = useCallback(() => {
    const key = loadedKeyRef.current
    lastCheckRef.current = Date.now()
    const markChecked = () => {
      // `data` does not change when the answer is the same as before, so mark the check here.
      checkedForRef.current = key
      if (mountedRef.current) rerender()
    }
    return refetch().then(markChecked, (error: unknown) => {
      markChecked()
      throw error
    })
  }, [refetch])

  useEffect(() => {
    if (!isVisible || !appDocsFresh) return
    if (Date.now() - lastCheckRef.current < VISIBLE_REFETCH_THROTTLE) return

    check().catch(() => {
      // Offline or a transient error: the next poll or visibility change tries again.
    })
  }, [isVisible, appDocsFresh, check])

  const recheckForRef = useRef<string | null>(null)
  useEffect(() => {
    // No result yet means the first check is still on its way; it counts for the copy loaded then.
    if (!appDocsFresh || !data || !loadedSinceCheck || recheckForRef.current === loadedKey) return

    recheckForRef.current = loadedKey
    check().catch(() => {
      // Offline or a transient error: `check` fell back to the last answer; the next poll or
      // visibility change asks again.
    })
  }, [appDocsFresh, data, loadedSinceCheck, loadedKey, check])

  const current = (data?.settingsVersion as CurrentSettingsVersion | undefined) ?? null
  if (!appDocsFresh || !current || loadedSinceCheck) {
    return { outdated: false, versionKey: null }
  }

  const outdated = isSettingsOutdated({ options: options?.settingsUpdated, ratesRev: rates?._rev }, current)

  return { outdated, versionKey: outdated ? JSON.stringify(current) : null }
}
